import { getToken, isLogin } from "../authorization/auth";
import { API_ENDPOINT, ENTERPRISE_DATA, USER_PERMISSION } from "../authorization/const";

/**
 * Resets cached data.
 * 
 * This function clears the local storage variables `ENTERPRISE_DATA` and `USER_PERMISSION`,
 * which are used for general caching purposes.
 */
export async function resetCache() {
    localStorage.removeItem(ENTERPRISE_DATA);
    localStorage.removeItem(USER_PERMISSION);
}

/**
 * Checks if the current user has a specific permission.
 * 
 * @param {string} permission - The permission to check.
 * @returns {Promise<boolean>} - Returns `true` if the user has the specified permission, otherwise `false`.
 */
export async function getUserAccess(permission) {
    const pr = await getAllUserAccess();
    return pr.includes(permission);
}

/**
 * Retrieves all access permissions available to the user.
 * 
 * @returns {Promise<string[]>} - A list of permission strings available to the user.
 * @throws {Error} - Throws an error if the API request fails.
 */
export async function getAllUserAccess() {
    if (!isLogin()) return [];
    let permission = localStorage.getItem(USER_PERMISSION);
    if (permission === null) {
        const res = await fetch(API_ENDPOINT + "user/get-scope/" + getToken());
        if (res.status !== 200) {
            throw await res.json();
        }
        permission = JSON.stringify(await res.json());
        localStorage.setItem(USER_PERMISSION, permission);
    }
    return JSON.parse(permission);
}

/**
 * Retrieves metadata for all office locations.
 * 
 * This function fetches office details as dictonary:
 * - `id`: Office ID
 * - `name`: Office name
 * - `logo`: Office logo URL
 * - `add_1`: Address line 1
 * - `add_2`: Address line 2
 * - `add_3`: Address line 3
 * - `pincode`: Postal code
 * - `phone`: Contact number
 * - `email`: Office email address
 * - `status`: Operational status
 * 
 * @returns {Promise<Object>} - An object containing office metadata.
 * @throws {Error} - Throws an error if the API request fails.
 */
export async function getOfficeMeta() {
    let org_data = localStorage.getItem(ENTERPRISE_DATA);
    if (org_data === null) {
        const res = await fetch(API_ENDPOINT + "organization/meta/" + getToken());
        if (res.status !== 200) {
            throw await res.json();
        }
        org_data = JSON.stringify(await res.json());
        localStorage.setItem(ENTERPRISE_DATA, org_data);
    }
    return JSON.parse(org_data);
}

/**
 * Get date in html form date input format
 * 
 * @returns Current date in YYYY-MM-DD format
 */
export function getCurrentDate() {
    const dt = new Date();
    return dt.getFullYear() + "-" + String(dt.getMonth() + 1).padStart(2, "0") + "-" + String(dt.getDate()).padStart(2, "0");
}

/**
 * Convert YYYY-MM-DD date string to DD-MM-YYYY date string. Works only on string
 * 
 * @param {string} date YYYY-MM-DD date string
 * @returns DD-MM-YYYY date format
 */
export function dateUsaToInd(date) {
    const dt = date.split("-");
    return dt[2] + "-" + dt[1] + "-" + dt[0];
}

/**
 * Converts a date string in the format YYYY-MM-DD to a Unix timestamp (seconds since epoch).
 * 
 * @param {string} date - The date string in YYYY-MM-DD format.
 * @returns {number} - The Unix timestamp representing the given date.
 */
export function dateUsaToTimestamp(date) {
    date = date.split("-");
    date = new Date(date[0], date[1] - 1, date[2]);
    return date.getTime() / 1000;
}

/**
 * Generates a string representing the first day of the current month in YYYY-MM-DD format.
 * 
 * @returns {string} - The first day of the current month.
 */
export function getCurrentMonthStart() {
    const dt = new Date();
    return dt.getFullYear() + "-" + String(dt.getMonth() + 1).padStart(2, "0") + "-01";
}

/**
 * Sorts an array of JSON objects based on a specified key in ascending order.
 * 
 * @param {Array<Object>} array - The array of objects to be sorted.
 * @param {string} key - The key to sort the array by.
 * @returns {Array<Object>} - The sorted array.
 */
export function sortJsonArrayByKey(array, key) {
    return array.sort(function (a, b) {
        var x = a[key]; var y = b[key];
        return ((x < y) ? -1 : ((x > y) ? 1 : 0));
    });
}

/**
 * Fetches warning data for a given page from an API.
 * 
 * @param {string|number} page - The page identifier.
 * @returns {Promise<Object|boolean>} - Resolves to the warning data if successful, otherwise returns false.
 */
export async function getPageWarning(page) {
    const res = await fetch(API_ENDPOINT + "warning/get-warning/" + page + "/" + getToken());
    if (res.status === 200)
        return await res.json();
    else if (res.status === 401)
        throw await res.json();
    return false;
}

/**
 * Sends a request to track page hits.
 * 
 * @param {string} page - The page identifier.
 * @returns {Promise<boolean>} - Resolves to true if the request succeeds, otherwise false.
 */
export async function hitTraffic(page) {
    const res = await fetch(API_ENDPOINT + "track-hit/" + page);
    if (res.status === 200)
        return true;
    return false;
}

/**
 * Convert DD-MMM-YYYY date to YYYY-MM-DD
 * 
 * @param {string} date DD-MMM-YYYY date string
 * @returns YYYY-MM-DD date format
 */
export function textDateToChrome(date) {
    const months = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05',
        Jun: '06', Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
    };
    const [day, mon, year] = date.split('-');
    const month = months[mon];
    return `${year}-${month}-${day}`;
}